import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Save, Send, Sparkles, Heart } from "lucide-react";
import { motion } from "framer-motion";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

// Compute the next Sunday at 14:00 UTC, formatted in the user's local timezone.
const getNextFertilityDigestDate = () => {
  const now = new Date();
  const next = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    14, 0, 0, 0
  ));
  // 0 = Sunday in JS
  const daysUntilSunday = (7 - now.getUTCDay()) % 7;
  next.setUTCDate(next.getUTCDate() + daysUntilSunday);
  if (next <= now) {
    next.setUTCDate(next.getUTCDate() + 7);
  }
  try {
    return next.toLocaleString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return next.toUTCString();
  }
};

// Friendly "X minutes/hours/days ago" string for an ISO timestamp.
const formatRelativeSent = (iso) => {
  if (!iso) return null;
  const sent = new Date(iso);
  const diffMs = Date.now() - sent.getTime();
  if (diffMs < 0) return null;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return sent.toLocaleDateString();
};

const DigestSection = ({ digestSettings, setDigestSettings, user, sendingDigest, sendingFertilityDigest, onSave, onSendTest, onSendFertilityNow }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}>
    <Card className="glass" data-testid="digest-settings-card">
      <CardHeader>
        <CardTitle className="text-lg text-white flex items-center gap-2">
          <Mail className="w-5 h-5 text-mystic-300" />
          Notifications & Digests
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-mystic-200/70 text-sm">
          Receive a weekly email with your moon forecast, cycle insights, and ritual recommendations.
        </p>
        <div className="flex items-center justify-between">
          <Label className="text-white">Enable Weekly Digest</Label>
          <Switch
            checked={digestSettings.weekly_digest_enabled}
            onCheckedChange={(v) => setDigestSettings(p => ({ ...p, weekly_digest_enabled: v }))}
            data-testid="digest-enabled-switch"
          />
        </div>
        {digestSettings.weekly_digest_enabled && (
          <>
            <div className="space-y-2">
              <Label className="text-mystic-200">Delivery Day</Label>
              <Select
                value={digestSettings.digest_day}
                onValueChange={(v) => setDigestSettings(p => ({ ...p, digest_day: v }))}
              >
                <SelectTrigger className="bg-mystic-800/50 border-mystic-600 text-white" data-testid="digest-day-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map(d => (
                    <SelectItem key={d} value={d} className="capitalize">
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-mystic-200">Email Address</Label>
              <input
                type="email"
                value={digestSettings.email || user?.email || ""}
                onChange={(e) => setDigestSettings(p => ({ ...p, email: e.target.value }))}
                className="w-full bg-mystic-800/50 border border-mystic-600 rounded-lg px-3 py-2 text-white text-sm focus:border-mystic-400 outline-none"
                placeholder="your@email.com"
                data-testid="digest-email-input"
              />
            </div>
          </>
        )}

        <div className="pt-2 border-t border-white/10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label className="text-white">Cycle Reminder Email</Label>
              <p className="text-mystic-200/60 text-xs mt-1 leading-relaxed">
                A gentle heads-up 2 days before your next predicted period.
              </p>
            </div>
            <Switch
              checked={digestSettings.cycle_reminders_enabled !== false}
              onCheckedChange={(v) => setDigestSettings(p => ({ ...p, cycle_reminders_enabled: v }))}
              data-testid="cycle-reminders-switch"
            />
          </div>
        </div>

        <div className="pt-2 border-t border-white/10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label className="text-white">Monthly Rhythm Summary</Label>
              <p className="text-mystic-200/60 text-xs mt-1 leading-relaxed">
                Monthly reflection email on the 1st of each month.
              </p>
            </div>
            <Switch
              checked={digestSettings.monthly_summary_enabled !== false}
              onCheckedChange={(v) => setDigestSettings(p => ({ ...p, monthly_summary_enabled: v }))}
              data-testid="monthly-summary-switch"
            />
          </div>
        </div>

        {/* Weekly Fertility Digest (TTC users) */}
        <div className="pt-2 border-t border-white/10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label className="text-white flex items-center gap-2">
                <Heart className="w-4 h-4 text-rose-300" />
                Weekly Fertility Digest
              </Label>
              <p className="text-mystic-200/60 text-xs mt-1 leading-relaxed">
                Sent every Sunday with your past 7 days of BBT, OPK, ovulation marks, and intimacy — plus an AI-generated insight. (TTC mode)
              </p>
              {digestSettings.fertility_digest_enabled !== false && (
                <p className="text-rose-200/80 text-xs mt-2 flex items-center gap-1" data-testid="next-fertility-digest-preview">
                  <Sparkles className="w-3 h-3" />
                  Next digest: {getNextFertilityDigestDate()}
                </p>
              )}
              {digestSettings.fertility_digest_last_sent_at && (
                <p className="text-mystic-200/60 text-xs mt-1" data-testid="last-fertility-digest-sent">
                  Last sent: {formatRelativeSent(digestSettings.fertility_digest_last_sent_at)}
                </p>
              )}
            </div>
            <Switch
              checked={digestSettings.fertility_digest_enabled !== false}
              onCheckedChange={(v) => setDigestSettings(p => ({ ...p, fertility_digest_enabled: v }))}
              data-testid="fertility-digest-switch"
            />
          </div>
          <Button
            onClick={onSendFertilityNow}
            variant="outline"
            size="sm"
            disabled={sendingFertilityDigest}
            className="mt-3 border-rose-400/40 text-rose-200 hover:bg-rose-500/10 hover:text-white rounded-full"
            data-testid="send-fertility-digest-now-btn"
          >
            <Send className="w-4 h-4 mr-1" />
            {sendingFertilityDigest ? "Sending..." : "Send my digest now"}
          </Button>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            onClick={onSave}
            className="bg-mystic-600 hover:bg-mystic-500 text-white rounded-full"
            data-testid="save-digest-btn"
          >
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
          <Button
            onClick={onSendTest}
            variant="outline"
            className="border-mystic-600 text-mystic-200 hover:text-white rounded-full"
            disabled={sendingDigest}
            data-testid="send-test-digest-btn"
          >
            <Send className="w-4 h-4 mr-1" /> {sendingDigest ? "Sending..." : "Send Test Email"}
          </Button>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

export default DigestSection;
